/**
 * PaymentPaywallModal - Main modal component for payment recovery
 *
 * Displays a full-screen overlay when users need to update their payment method
 * or create a new subscription.
 *
 * Supports two flows based on flowType:
 * - 'update_payment_method': For dunning/past_due - uses SetupIntent (card only)
 * - 'create_subscription': For canceled users - uses PaymentIntent with Express Checkout
 *
 * Also supports a cancellation flow for past_due users:
 * - Cancel Subscription button shows on info step
 * - Leads to cancel reason selection -> winback offer -> cancel confirmation or payment
 *
 * Usage:
 * ```tsx
 * const { shouldShowPaywall, context: reason, subscriptionId, flowType, price } = usePaywallDetection();
 *
 * {shouldShowPaywall && (
 *   <PaymentPaywallModal
 *     reason={reason}
 *     subscriptionId={subscriptionId}
 *     flowType={flowType}
 *     price={price}
 *     onSuccess={() => navigate(ROUTE_DASHBOARD)}
 *   />
 * )}
 * ```
 */

import { Elements } from '@stripe/react-stripe-js';
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import type {
  PaywallPriceResponse,
  CancellationReason,
} from '@/api/generated-catch-backend/model';
import {
  buildPriceData,
  PriceDataType,
} from '@/app/components/payment/components/method/payment-method-v2.controller';
import usePaymentController from '@/app/components/payment/payment.controller';
import MembershipSummary from '@/app/components/ppra/components/ppra-payment-page/components/MembershipSummary';
import FeatureList from '@/app/components/ppra/components/ppra-payment-page/components/FeatureList';
import { PaywallHeader, PaywallReason } from './components/PaywallHeader';
import { PaywallPaymentForm } from './components/PaywallPaymentForm';
import { PaywallSuccessView } from './components/PaywallSuccessView';
import { PaywallErrorView } from './components/PaywallErrorView';
import { CancelReasonStep } from './components/CancelReasonStep';
import { WinbackOfferStep } from './components/WinbackOfferStep';
import { WinbackPriceCard } from './components/WinbackPriceCard';
import { useUpdatePaymentMethod } from './useUpdatePaymentMethod';
import { useCreateSubscription } from './useCreateSubscription';
import { usePaywallCancellation } from './usePaywallCancellation';
import { useDownloadLabResults } from '@/app/components/labs-order/hooks/useDownloadLabResults';
import useAnalyticsCalls from '@/hooks/use-analytics';
import type { PaywallFlowType } from './usePaywallDetection';
import './paywall-reset.css';

interface PaymentPaywallModalProps {
  reason: PaywallReason | null;
  subscriptionId: string | null;
  flowType: PaywallFlowType | null;
  price: PaywallPriceResponse | null;
  labOrderId: string | null;
  onSuccess: () => void;
}

type PaywallStep =
  | 'info'
  | 'payment'
  | 'cancel_reason'
  | 'winback_offer'
  | 'cancelled';

function PaymentPaywallModalContent({
  reason,
  subscriptionId,
  flowType,
  price,
  labOrderId,
  onSuccess,
}: PaymentPaywallModalProps) {
  const [step, setStep] = useState<PaywallStep>('info');
  const [showError, setShowError] = useState(false);
  // Track if user came to payment from winback acceptance (to show winback pricing)
  const [isWinbackPayment, setIsWinbackPayment] = useState(false);
  const { downloadResults, isDownloading } = useDownloadLabResults();
  const { callProductPaywall, callProductPaymentPage } = useAnalyticsCalls();

  // Cancellation flow state
  const cancellation = usePaywallCancellation(subscriptionId);

  // Show cancel button for update_payment_method flow (dunning/past_due)
  const showCancelButton = flowType === 'update_payment_method';

  // Compute paywall type for analytics
  const paywallType = useMemo(() => {
    if (flowType === 'update_payment_method') return 'Fix Payment' as const;
    // create_subscription flow
    if (reason?.isPaymentFailed) return 'Fix Payment + Cancel' as const;
    return 'Subscribe' as const;
  }, [flowType, reason]);

  // Track Product Paywall on mount (fire once only)
  const hasTrackedPaywall = useRef(false);
  useEffect(() => {
    if (!hasTrackedPaywall.current) {
      callProductPaywall(paywallType);
      hasTrackedPaywall.current = true;
    }
  }, [callProductPaywall, paywallType]);

  // Build price data from the paywall context (already fetched by usePaywallDetection)
  const priceData: PriceDataType | null = useMemo(() => {
    if (!price) return null;
    return buildPriceData(price.subtotal, price.discount, price.tax, price.total);
  }, [price]);

  const handleError = useCallback(() => {
    setShowError(true);
  }, []);

  // Determine which flow to use
  const isCreateSubscriptionFlow = flowType === 'create_subscription';

  // For update_payment_method flow, use SetupIntent to update payment method
  const updatePaymentHook = useUpdatePaymentMethod({
    subscriptionId: subscriptionId ?? '',
    onError: handleError,
  });

  // For create_subscription flow, create a new subscription
  const createSubscriptionHook = useCreateSubscription({
    onError: handleError,
  });

  // Use the appropriate hook based on flow type
  const {
    state,
    error,
    reset,
  } = isCreateSubscriptionFlow ? createSubscriptionHook : updatePaymentHook;

  // Get base submit handlers based on flow type
  const baseSubmitHandler = isCreateSubscriptionFlow
    ? createSubscriptionHook.createSubscription
    : updatePaymentHook.updatePaymentMethod;

  const baseExpressCheckoutHandler = isCreateSubscriptionFlow
    ? createSubscriptionHook.handleExpressCheckout
    : updatePaymentHook.handleExpressCheckout;

  // Wrap submit handler to call acceptWinback first when in winback mode
  const handleSubmit = useCallback(
    async (zipCode?: string) => {
      // If in winback flow, apply the discount first
      if (isWinbackPayment && subscriptionId) {
        try {
          await cancellation.acceptWinback(subscriptionId);
        } catch {
          // Error handled in hook, show error but don't proceed
          setShowError(true);
          return;
        }
      }
      await baseSubmitHandler(zipCode);
    },
    [isWinbackPayment, subscriptionId, cancellation, baseSubmitHandler]
  );

  // Wrap express checkout to call acceptWinback first when in winback mode
  const handleExpressCheckout = useCallback(
    async (event: import('@stripe/stripe-js').StripeExpressCheckoutElementConfirmEvent) => {
      if (isWinbackPayment && subscriptionId) {
        try {
          await cancellation.acceptWinback(subscriptionId);
        } catch {
          setShowError(true);
          return;
        }
      }
      await baseExpressCheckoutHandler(event);
    },
    [isWinbackPayment, subscriptionId, cancellation, baseExpressCheckoutHandler]
  );

  const handleRetry = useCallback(() => {
    setShowError(false);
    reset();
  }, [reset]);

  const handleProceedToPayment = useCallback(() => {
    callProductPaymentPage('paywall');
    setStep('payment');
  }, [callProductPaymentPage]);

  // Cancellation flow handlers
  const handleCancelClick = useCallback(() => {
    setStep('cancel_reason');
  }, []);

  const handleCancelReasonSubmit = useCallback(
    async (cancelReason: CancellationReason, reasonDescription?: string) => {
      if (!subscriptionId) return;
      try {
        await cancellation.submitCancelReason(
          subscriptionId,
          cancelReason,
          reasonDescription
        );
        setStep('winback_offer');
      } catch {
        // Error is handled in the hook
      }
    },
    [subscriptionId, cancellation]
  );

  const handleWinbackAccept = useCallback(() => {
    // Go directly to payment step with winback pricing displayed
    // The actual discount application happens when payment is submitted
    setIsWinbackPayment(true);
    setStep('payment');
  }, []);

  const handleWinbackDecline = useCallback(async () => {
    if (!subscriptionId) return;
    try {
      await cancellation.declineWinback(subscriptionId);
      setStep('cancelled');
    } catch {
      // Error is handled in the hook
    }
  }, [subscriptionId, cancellation]);

  const handleBackToInfo = useCallback(() => {
    cancellation.reset();
    setStep('info');
  }, [cancellation]);

  const isSuccess = state === 'success';

  // Default price data while loading
  const displayPriceData = priceData ?? buildPriceData(0, 0, 0, 0);

  // Build winback price data from cancellation pricing
  const winbackPriceData: PriceDataType | null = useMemo(() => {
    if (!cancellation.winbackPricing) return null;
    const { originalPriceCents, discountCents, finalPriceCents } = cancellation.winbackPricing;
    // Build price data with discount applied
    return buildPriceData(originalPriceCents, discountCents, 0, finalPriceCents);
  }, [cancellation.winbackPricing]);

  // Use winback pricing on payment step if user came from winback acceptance
  const paymentStepPriceData = isWinbackPayment && winbackPriceData
    ? winbackPriceData
    : displayPriceData;

  // CTA button text - always "Update Payment" on info screen
  const ctaButtonText = 'Update Payment';

  // Render based on step
  const renderContent = () => {
    if (isSuccess) {
      return <PaywallSuccessView onNavigateToDashboard={onSuccess} />;
    }

    switch (step) {
      case 'info':
        return (
          <div className="paywall-reset flex flex-col gap-5">
            {/* Header message */}
            {reason && <PaywallHeader reason={reason} />}

            {/* Membership pricing card */}
            <MembershipSummary
              priceData={displayPriceData}
              isCouponVisible={false}
              showCoupon={() => undefined}
              coupon=""
              setCoupon={() => undefined}
              couponValidationError={null}
              isSubmitting={false}
              getPrice={() => Promise.resolve()}
              isSinglePaymentEnabled={false}
              isTrial={false}
              isPriceLoading={!price}
            />

            {/* Feature list */}
            <FeatureList />

            {/* CTA Button */}
            <button
              onClick={handleProceedToPayment}
              className="flex w-full items-center justify-center rounded-full bg-[#7CF1C7] px-5 py-5 text-sm font-bold leading-[1.4] text-[#183344] transition-opacity hover:opacity-90"
            >
              {ctaButtonText}
            </button>

            {/* Cancel Subscription button - only for dunning/past_due users */}
            {showCancelButton && (
              <button
                onClick={handleCancelClick}
                className="w-full text-center text-sm font-medium text-[#5A6B76] transition-opacity hover:text-[#183344]"
              >
                Cancel Subscription
              </button>
            )}

            {/* Download Raw Results link - only shown when user has processed labs */}
            {labOrderId && (
              <button
                onClick={() => downloadResults(labOrderId)}
                disabled={isDownloading}
                className="mt-2 w-full text-center text-sm font-medium text-[#183344] underline transition-opacity hover:opacity-70 disabled:opacity-50"
              >
                {isDownloading ? 'Downloading...' : 'Download Raw Results'}
              </button>
            )}
          </div>
        );

      case 'cancel_reason':
        return (
          <CancelReasonStep
            onSubmit={handleCancelReasonSubmit}
            onBack={handleBackToInfo}
            isSubmitting={cancellation.isSubmitting}
            error={cancellation.error}
          />
        );

      case 'winback_offer':
        return (
          <WinbackOfferStep
            pricing={cancellation.winbackPricing}
            isPricingLoading={cancellation.isPricingLoading}
            onAccept={handleWinbackAccept}
            onDecline={handleWinbackDecline}
            isSubmitting={cancellation.isSubmitting}
            error={cancellation.error}
          />
        );

      case 'cancelled':
        return (
          <div className="paywall-reset flex flex-col items-center gap-6 py-8 text-center">
            <h2 className="text-2xl font-bold text-[#183344]">
              Your subscription has been cancelled.
            </h2>
            <button
              onClick={onSuccess}
              className="flex w-full items-center justify-center rounded-full bg-[#183344] px-5 py-4 text-sm font-bold text-white transition-opacity hover:opacity-90"
            >
              OK
            </button>
          </div>
        );

      case 'payment':
      default: {
        // Get the price amount for the payment form
        const paymentPriceAmount = isWinbackPayment
          ? cancellation.winbackPricing?.finalPriceCents
          : price?.total;

        // Show either payment error or cancellation error (for winback flow)
        const displayError = error || (isWinbackPayment ? cancellation.error : null);

        return (
          <div className="flex flex-col gap-5">
            {/* Error message */}
            {(showError && displayError) && (
              <PaywallErrorView errorMessage={displayError} onRetry={handleRetry} />
            )}

            {/* Membership pricing card - use WinbackPriceCard for winback flow */}
            {isWinbackPayment ? (
              <WinbackPriceCard
                pricing={cancellation.winbackPricing}
                isLoading={cancellation.isPricingLoading}
              />
            ) : (
              <MembershipSummary
                priceData={paymentStepPriceData}
                isCouponVisible={false}
                showCoupon={() => undefined}
                coupon=""
                setCoupon={() => undefined}
                couponValidationError={null}
                isSubmitting={false}
                getPrice={() => Promise.resolve()}
                isSinglePaymentEnabled={false}
                isTrial={false}
                isPriceLoading={!price}
              />
            )}

            {/* Payment form - different flows based on flowType */}
            <PaywallPaymentForm
              state={state}
              onSubmit={handleSubmit}
              flowType={flowType}
              priceAmount={paymentPriceAmount}
              onExpressCheckout={handleExpressCheckout}
            />
          </div>
        );
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/60 p-4">
      <div className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Scrollable content area */}
        <div className="max-h-[90vh] overflow-y-auto px-7 py-10">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

export function PaymentPaywallModal(props: PaymentPaywallModalProps) {
  const [amount, setAmount] = useState<number | undefined>();

  // Listen for amount updates from price fetching
  useEffect(() => {
    const handleAmountUpdate = (event: CustomEvent<{ amount: number }>) => {
      setAmount(event.detail.amount);
    };

    window.addEventListener('stripe-amount-update', handleAmountUpdate as EventListener);
    return () => {
      window.removeEventListener('stripe-amount-update', handleAmountUpdate as EventListener);
    };
  }, []);

  // Use shared Stripe configuration from payment controller
  // - update_payment_method: uses 'setup' mode (save card without charging)
  // - create_subscription: uses 'subscription' mode (charge for subscription)
  const stripeMode = props.flowType === 'create_subscription' ? 'subscription' : 'setup';
  const { stripePromise, stripeOptions } = usePaymentController({
    mode: stripeMode,
    amount,
  });

  return (
    <Elements stripe={stripePromise} options={stripeOptions} key={`${props.flowType}-${amount}`}>
      <PaymentPaywallModalContent {...props} />
    </Elements>
  );
}
